import { calc } from '../../../../../../../../../helpers/numbers'

import {
  AD_PLACEMENTS,
  AD_UNITS,
  ALLOCATION_TYPE,
  allocationTypes,
  allocationGroups,
  BILLING_EVENT,
  interchangeableAllocationTypes,
  INVENTORY_TYPE,
  INVENTORY_TYPE_AD_PLACEMENTS,
  IS_SUB_ALLOCATION
} from '../fields'
import { ALLOCATION } from '../../../../../fields'
import { CPM, CPC, VIEWABLE_CPM } from './InventoryAccessAndAllocationSection/SubAllocationTables/fields'

export const getShowTotalRightValue = allocationType => {
  return (
    allocationType === allocationTypes.impressions ||
    allocationType === allocationTypes.clicks ||
    allocationType === allocationTypes.viewable_impressions ||
    allocationType === allocationTypes.budget
  )
}

export const getFilteredAdUnits = values => {
  return values[AD_UNITS].filter(adUnit => {
    const parent = adUnit.parent
    const hasSelectedParent = values[AD_UNITS].some(selectedAdUnit => selectedAdUnit.id === parent)
    // We want to filter out ad units that have a parent that is also selected in the ad units list
    return !hasSelectedParent
  })
}

export const calculateTotalAllocation = values => {
  const { allocationGroup } = getAllocationInfo(values)

  // For single allocation, just return the allocation value
  if (!values[IS_SUB_ALLOCATION]) {
    return getAllocationValue(values, allocationGroup)
  }

  // For sub-allocation, sum up all allocation values
  if (values[INVENTORY_TYPE] === INVENTORY_TYPE_AD_PLACEMENTS) {
    return values[AD_PLACEMENTS].reduce((total, placement) => {
      const allocationValue = getAllocationValue(placement, allocationGroup)
      return Number(total) + Number(allocationValue)
    }, 0)
  } else {
    const filteredAdUnits = getFilteredAdUnits(values)
    return filteredAdUnits.reduce((total, adUnit) => {
      const allocationValue = getAllocationValue(adUnit, allocationGroup)
      return Number(total) + Number(allocationValue)
    }, 0)
  }
}

export const getBillingEventType = (allocationType, billingEvent) => {
  if (allocationType === allocationTypes.budget) {
    return interchangeableAllocationTypes[billingEvent]
  } else {
    return interchangeableAllocationTypes[allocationType]
  }
}

export const getAllocationInfo = values => {
  const allocationType = values[ALLOCATION_TYPE]
  const billingEvent = values[BILLING_EVENT]
  const billingEventType = getBillingEventType(allocationType, billingEvent)
  const allocationGroup = allocationGroups[allocationType]
  const isPercentageAllocation = allocationGroups[allocationType] === 'percentage'

  return {
    allocationType,
    billingEventType,
    isPercentageAllocation,
    allocationGroup
  }
}

// Helper to get allocation value from group structure
export const getAllocationValue = (item, allocationGroup) => {
  return item?.[allocationGroup]?.[ALLOCATION] || 0
}

// Helper to safely get rate value from item
export const getRateValue = (item, billingEventType) => {
  return Number(item[billingEventType] || 0)
}

export const getIsBudgetAllocationType = right => {
  return right[ALLOCATION_TYPE] === allocationTypes.budget
}

export const getRateFieldLabel = billingEventType => {
  switch (billingEventType) {
    case CPC:
      return 'CPC'
    case VIEWABLE_CPM:
      return 'vCPM'
    case CPM:
    default:
      return 'CPM'
  }
}

export const getAllocationSymbol = (isPercentageAllocation, allocationType, currencySymbol) => {
  if (isPercentageAllocation) {
    return '%'
  } else if (allocationType === allocationTypes.budget) {
    return currencySymbol
  }
  return undefined
}

export const calculateTotalRightValue = values => {
  const { allocationType, billingEventType, allocationGroup } = getAllocationInfo(values)

  // For budget allocation type, the total value is the sum of all budget sub-allocations
  if (allocationType === allocationTypes.budget) {
    if (!values[IS_SUB_ALLOCATION]) {
      // For single allocation with budget type, return the allocation value directly
      return getAllocationValue(values, allocationGroup)
    }

    // For sub-allocation with budget type, sum up all allocation values
    if (values[INVENTORY_TYPE] === INVENTORY_TYPE_AD_PLACEMENTS) {
      return values[AD_PLACEMENTS].reduce((total, placement) => {
        const allocationValue = getAllocationValue(placement, allocationGroup)
        return total + allocationValue
      }, 0)
    } else {
      const filteredAdUnits = getFilteredAdUnits(values)
      return filteredAdUnits.reduce((total, adUnit) => {
        const allocationValue = getAllocationValue(adUnit, allocationGroup)
        return total + allocationValue
      }, 0)
    }
  }

  // For single allocation, calculate total value using the single allocation value and rate
  if (!values[IS_SUB_ALLOCATION]) {
    const allocationValue = getAllocationValue(values, allocationGroup)
    const rateValue = getRateValue(values, billingEventType)

    return calc(rateValue).mul(allocationValue).div(1000).toNumber()
  }

  // For sub-allocation, calculate total value by summing up all allocation values multiplied by their rates
  if (values[INVENTORY_TYPE] === INVENTORY_TYPE_AD_PLACEMENTS) {
    return values[AD_PLACEMENTS].reduce((total, placement) => {
      const allocationValue = getAllocationValue(placement, allocationGroup)
      const rateValue = getRateValue(placement, billingEventType)

      return calc(total).add(calc(rateValue).mul(allocationValue).div(1000)).toNumber()
    }, 0)
  } else {
    const filteredAdUnits = getFilteredAdUnits(values)

    return filteredAdUnits.reduce((total, adUnit) => {
      const allocationValue = getAllocationValue(adUnit, allocationGroup)
      const rateValue = getRateValue(adUnit, billingEventType)

      return calc(total).add(calc(rateValue).mul(allocationValue).div(1000)).toNumber()
    }, 0)
  }
}
